import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { duplicateEvent, getDuplicationJob, searchEvents } from './credit-service';

const getDuplicateEventSearchBaseQueryKey = orgId => [
  'organization',
  orgId,
  'duplicate-event-search',
];

const duplicateEventSearchOptions = (orgId, search, enabled) => ({
  queryKey: [...getDuplicateEventSearchBaseQueryKey(orgId), search],
  queryFn: () => searchEvents(search, orgId),
  enabled,
  keepPreviousData: true,
  select: results => results.map(({ id, name, shortcode: code }) => ({ id, code, name })),
});

export const useDuplicateEventSearch = (orgId, search, enabled = true) =>
  useQuery(duplicateEventSearchOptions(orgId, search, enabled));

export const useCreateDuplicateEvent = orgId => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['organization', orgId, 'duplicate-event'],
    mutationFn: ({ data, duplicationConfig }) => duplicateEvent(data, orgId, duplicationConfig),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: getDuplicateEventSearchBaseQueryKey(orgId) }),
  });
};

export const JOB_STATUS = {
  PENDING: 'PENDING',
  RUNNING: 'RUNNING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  PARTIALLY_FAILED: 'PARTIALLY_FAILED',
};

const JOB_TERMINAL_STATUSES = [
  JOB_STATUS.COMPLETED,
  JOB_STATUS.FAILED,
  JOB_STATUS.PARTIALLY_FAILED,
];
const JOB_POLL_INTERVAL = 4000;

export const useDuplicationJob = (eventId, jobId) =>
  useQuery({
    queryKey: ['event', eventId, 'duplication-job', jobId],
    queryFn: () => getDuplicationJob(eventId, jobId),
    enabled: !!eventId && !!jobId,
    retry: false,
    refetchInterval: (job, query) =>
      query?.state.error || (job && JOB_TERMINAL_STATUSES.includes(job.status))
        ? false
        : JOB_POLL_INTERVAL,
  });
